import axios from "axios";
import { toast } from "react-toastify";

// Base principal para endpoints de apps
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const API_URL = `${BASE_URL}/api/`;
export const API_AUTH_URL = `${BASE_URL}/auth/`;
export const API_PAGOS_URL = `${BASE_URL}/webhooks/`;

// AVISOS TOSTIFY UNIFICADOS
// ==============================
// 🟢 MAPA DE ERRORES UNIFICADO
// ==============================
const ERROR_MAP = {
  // 403 – Sesiones
  "Authentication credentials were not provided.":
    "🔒 Tu sesión expiró (credenciales inválidas). Vuelve a iniciar sesión.",

  "CSRF Failed: CSRF token missing or incorrect.":
    "⚠️ Tu sesión expiró (token CSRF inválido). Vuelve a iniciar sesión.",

  // 400 – Bad Request / Validation
  "Bad Request":
    "⚠️ Los datos enviados no son válidos. Revisa el formulario.",

  // 413 – Archivo demasiado grande
  "Request Entity Too Large":
    "📁 Archivo demasiado grande. Reduce el tamaño e intenta de nuevo.",

  // 401 – Unauthorized
  "Unauthorized":
    "⛔ No tienes autorización para realizar esta acción.",

  // 404 – Not Found
  "Not found.":
    "❓ Recurso no encontrado.",

  // 429 – Too Many Requests
  "Request was throttled.":
    "⏳ Demasiadas solicitudes. Intenta de nuevo en unos segundos.",

  // 500 – Internal Server Error
  "Internal Server Error":
    "💥 Ocurrió un error interno en el servidor.",
};


// Función para obtener el token de cookies
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(name + "=")) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Crear una sola instancia principal
const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true, // 🔐 importante para enviar cookies
});

// Interceptor para agregar CSRF token en métodos sensibles
api.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }
  }
  return config;
});

export default api;

// Mostrar alertas automáticas si viene un mensaje desde el backend (ej: middleware de suscripción)
api.interceptors.response.use(
  response => response,
  error => {
    const status = error?.response?.status;
    const backendDetail = error?.response?.data?.detail;

    let finalMessage = null;

    // ============================================
    // 1) Si el backend envía un "detail"
    // ============================================
    if (backendDetail) {
      // Si existe en el mapa → usamos tu texto bonito
      if (ERROR_MAP[backendDetail]) {
        finalMessage = ERROR_MAP[backendDetail];
      } else {
        // Si no coincide → usamos el texto original del backend
        finalMessage = backendDetail;
      }
    }

    // ============================================
    // 2) Si NO hay detail, usamos el status HTTP
    // ============================================
    if (!finalMessage) {
      switch (status) {
        case 400:
          finalMessage = "⚠️ Los datos enviados no son válidos.";
          break;
        case 401:
        case 403:
          finalMessage = "🔒 Tu sesión expiró. Vuelve a iniciar sesión.";
          break;
        case 413:
          finalMessage = "📁 Archivo demasiado grande.";
          break;
        case 500:
          finalMessage = "💥 Error interno del servidor.";
          break;
        case 504:
          finalMessage = "⏳ Timeout: El servidor tardó demasiado.";
          break;
        case 404:
          finalMessage = "❓ No se encontró el recurso solicitado.";
          break;
        case 429:
          finalMessage = "⏳ Muchas solicitudes. Intenta de nuevo.";
          break;
        default:
          finalMessage = `⚠️ Error inesperado (${status || "sin código"}).`;
      }
    }

    // ============================================
    // 3) Sin response → red / timeout / servidor caído
    // ============================================
    if (!error.response) {
      finalMessage = "🌐 Error de conexión o servidor no disponible.";
    }

    // EXCEPCIÓN ESPECIAL PARA 404 DEL HISTORIAL
    if (
      status === 404 &&
      error?.config?.url?.includes("historias-clinicas/por_paciente")
    ) {
      return Promise.reject(error); // NO mostrar toast
    }
    // Excepcion de video onboarding: no mostrar toast si falla la consulta de video
    if (error?.config?.url?.includes("onboarding/video")) {
      return Promise.reject(error); // NO mostrar toast
    }
    // Excepción del archivo de intercambio: el modal muestra el motivo real.
    // Solo se deja el aviso general si la sesión expiró (401/403).
    if (
      error?.config?.url?.includes("exportar-nom") &&
      status !== 401 &&
      status !== 403
    ) {
      return Promise.reject(error); // NO mostrar toast
    }
    // ============================================
    // 4) Enviar mensaje por Toastify (fallback alert)
    // ============================================
    try {
      toast.error(finalMessage);
    } catch {
      alert(finalMessage);
    }

    return Promise.reject(error);
  }
);



//
// ==========================
// 🟣 API DE PAGOS (Stripe)
// ==========================
//
export const pagosAPI = axios.create({
  baseURL: API_PAGOS_URL, // Endpoint directo a /webhooks/
  withCredentials: true,
});

// 🚨 Agrega CSRF también para pagos (Stripe)
pagosAPI.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
    const csrfToken = getCookie("csrftoken");
    if (csrfToken) {
      config.headers["X-CSRFToken"] = csrfToken;
    }
  }
  return config;
});